import React, { Fragment, useContext, useId, useState } from 'react';

import { number2kanji } from '@geolonia/japanese-numeral';

import Chapter from './Chapter';
import FileLink from './FileLink';
import FormField from './FormField';
import SettingsContext from './SettingsContext';
import normalizeText from '../../utils/normalize_text';

export default function App({ thesis: _thesis, settings }) {
  const [thesis, setThesis] = useState(_thesis);
  const [tempId, setTempId] = useState(-2);

  const chapters     = thesis.thesis_bodies;
  const liveChapters = chapters.filter((chapter) => !chapter._destroy);

  const totalCharacters = liveChapters.reduce((acc, chapter) => {
    const stripped = (chapter.body || '').replace(/<[^>]+>/g, '');

    return acc + normalizeText(stripped).length;
  }, 0);

  function setSubTitle(subTitle) {
    setThesis({ ...thesis, theme_sub_title: subTitle });
  }

  function setChapters(chapters) {
    setThesis({ ...thesis, thesis_bodies: chapters });
  }

  function addChapter(i) {
    setChapters(chapters.toSpliced(i + 1, 0, { temp_id: tempId }));
    setTempId(tempId - 1);
  }

  function removeChapter(i) {
    const { id, temp_id } = chapters[i];

    if (id) {
      setChapters(chapters.toSpliced(i, 1, { id, temp_id, _destroy: true }));
    } else {
      setChapters(chapters.toSpliced(i, 1));
    }
  }

  function modifyChapter(i, props) {
    const chapter = chapters[i];

    setChapters(chapters.toSpliced(i, 1, { ...chapter, ...props }));
  }

  function setReference(reference) {
    setThesis({ ...thesis, reference });
  }

  return (
    <SettingsContext.Provider value={settings}>
      <div className='d-flex align-items-start gap-3' style={{paddingBottom: 'calc(var(--toolbar-height) + 1rem)'}}>
        <div className='flex-fill'>
          <SubTitleField subTitle={thesis.theme_sub_title} setSubTitle={setSubTitle} />

          {chapters.map((chapter, i) => {
            const number = liveChapters.indexOf(chapter) + 1;

            return (
              <Chapter
                key={chapter.temp_id}
                index={i}
                number={number}
                chapter={chapter}
                totalChapters={liveChapters.length}
                addChapter={addChapter}
                removeChapter={removeChapter}
                modifyChapter={modifyChapter}
              />
            );
          })}

          <ReferenceField reference={thesis.reference} setReference={setReference} />
        </div>

        <TOC chapters={liveChapters} />
      </div>

      <Toolbar totalCharacters={totalCharacters} />
      <PreviewModal thesis={thesis} />
      <CommentModal />
    </SettingsContext.Provider>
  );
}

function SubTitleField({ subTitle, setSubTitle }) {
  const id = useId();

  return (
    <FormField label='サブタイトル' id={id}>
      <input
        type='text'
        id={id}
        name='graduation_thesis[theme_sub_title]'
        value={subTitle || ''}
        onChange={(e) => {
          setSubTitle(e.target.value);
        }}
      />
    </FormField>
  );
}

function ReferenceField({ reference, setReference }) {
  const id = useId();

  return (
    <FormField label='引用文献' id={id}>
      <textarea
        id={id}
        name='graduation_thesis[reference]'
        value={reference || ''}
        onChange={(e) => {
          setReference(e.target.value);
        }}
        rows='5'
      />
    </FormField>
  );
}

function Toolbar({ totalCharacters }) {
  const { can_submit, submitted_status, comment } = useContext(SettingsContext);

  return (
    <div className='fixed-bottom fixed-responsive bg-white bg-opacity-75 d-flex justify-content-between p-3'>
      <div className='btn-group flex-fill justify-content-center'>
        {comment && (
          <div className='btn-wrap'>
            <button type='button' className='btn-commentary' data-bs-toggle='modal' data-bs-target='#commentModal'>講評</button>
          </div>
        )}

        <div className='btn-wrap'>
          <button type='button' className='btn-preview' data-bs-toggle='modal' data-bs-target='#previewModal'>プレビュー</button>
        </div>

        <div className='btn-wrap'>
          <button className='btn-add' name='graduation_thesis[status]' value='draft'>一時保存</button>
        </div>

        <div className='btn-wrap'>
          <a href='/' className='btn-back' data-turbo-confirm='戻ります。一時保存をしていないと入力内容が消えますがよろしいですか？'>戻る</a>
        </div>
      </div>

      <div>
        <div className='mb-2'>
          <span className='text-kuled'>総文字数チェック：</span> {totalCharacters}字
        </div>

        {can_submit && (
          <button className='btn-submit-lg' name='graduation_thesis[status]' value={submitted_status} data-turbo-confirm='提出します。提出後は修正できませんがよろしいですか？'>提出</button>
        )}
      </div>
    </div>
  );
}

function TOC({ chapters }) {
  return (
    <ul className='flex-shrink-0 position-sticky bg-blue-light rounded p-3 mb-0 w-25 d-none d-lg-block' style={{top: 'calc(var(--header-height) + 1rem)', right: '0', maxHeight: 'calc(100vh - calc(var(--header-height) + 1rem) - calc(var(--toolbar-height) + 1rem))', overflowY: 'auto'}}>
      {chapters.map((chapter, i) => {
        const number = i + 1;

        return (
          <li key={chapter.temp_id} style={{listStyle: 'revert', padding: 'revert'}}>
            <a href={`#chapter-${number}`} className='text-break' data-turbo='false'>
              第{number2kanji(number)}章 {chapter.title}
            </a>
          </li>
        );
      })}
    </ul>
  );
}

function PreviewModal({ thesis }) {
  const { title } = useContext(SettingsContext);

  return (
    <div id='previewModal' className='modal fade' tabIndex='-1'>
      <div className='modal-dialog modal-dialog-scrollable'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>{title}</h5>
          </div>

          <div className='modal-body'>
            <FormField label='サブタイトル'>
              {thesis.theme_sub_title}
            </FormField>

            {thesis.thesis_bodies.map((chapter, i) => {
              const number = i + 1;

              return (
                <Fragment key={chapter.temp_id}>
                  <div id={`preview-${number}`}>
                    <FormField label={`第${number2kanji(number)}章`}>
                      {chapter.title}
                    </FormField>

                    <FormField label='本文'>
                      <div className='ql-snow'>
                        <div className='ql-editor' dangerouslySetInnerHTML={{ __html: chapter.body }} />
                      </div>
                    </FormField>

                    <FormField label='添付ファイル'>
                      <FileLink newFile={chapter.new_file} savedFile={chapter.saved_file} />
                    </FormField>

                    <FormField label='注釈'>
                      <div className='pre-wrap-text'>
                        {chapter.notes}
                      </div>
                    </FormField>
                  </div>

                  <hr />
                </Fragment>
              );
            })}

            <FormField label='引用文献'>
              <div className='pre-wrap-text'>
                {thesis.reference}
              </div>
            </FormField>
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-secondary' data-bs-dismiss='modal'>閉じる</button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CommentModal() {
  const { comment } = useContext(SettingsContext);

  return (
    <div id='commentModal' className='modal fade' tabIndex='-1'>
      <div className='modal-dialog modal-dialog-scrollable'>
        <div className='modal-content'>
          <div className='modal-header'>
            <h5 className='modal-title'>講評</h5>
          </div>

          <div className='modal-body' style={{whiteSpace: 'pre-wrap'}}>
            {comment}
          </div>

          <div className='modal-footer'>
            <button type='button' className='btn btn-secondary' data-bs-dismiss='modal'>閉じる</button>
          </div>
        </div>
      </div>
    </div>
  );
}
